import { ReactNode } from 'react';

/**
 * Ícones de linha do rebranding (handoff Icons.jsx), portados para TSX.
 * Traço 1.8, viewBox 24 — mesmo estilo visual do design system novo.
 */

interface IconProps {
  size?: number;
}

interface ColorIconProps extends IconProps {
  color?: string;
}

function LineIcon({ size = 20, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

export function IconDashboard(props: IconProps) {
  return (
    <LineIcon {...props}>
      <rect x="3" y="3" width="7" height="8" rx="1.2" />
      <rect x="14" y="3" width="7" height="5" rx="1.2" />
      <rect x="14" y="11" width="7" height="10" rx="1.2" />
      <rect x="3" y="14" width="7" height="7" rx="1.2" />
    </LineIcon>
  );
}

export function IconRecon(props: IconProps) {
  return (
    <LineIcon {...props}>
      <circle cx="12" cy="9" r="5" />
      <path d="M8.5 13.2 7 21l5-2.6L17 21l-1.5-7.8" />
    </LineIcon>
  );
}

export function IconGaleria(props: IconProps) {
  return (
    <LineIcon {...props}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="8.5" cy="9.5" r="1.8" />
      <path d="M4 17l4.5-4 3.5 3 3-2.5L20 17" />
    </LineIcon>
  );
}

export function IconFeed(props: IconProps) {
  return (
    <LineIcon {...props}>
      <path d="M21 11.5a7.5 7.5 0 0 1-10.8 6.7L4 20l1.8-5.3A7.5 7.5 0 1 1 21 11.5z" />
      <path d="M9 10.5h6M9 13.5h4" />
    </LineIcon>
  );
}

export function IconDestaques(props: IconProps) {
  return (
    <LineIcon {...props}>
      <path d="M12 4l2.3 4.7 5.2.8-3.8 3.7.9 5.2-4.6-2.5-4.6 2.5.9-5.2L4.5 9.5l5.2-.8z" />
    </LineIcon>
  );
}

export function IconEquipe(props: IconProps) {
  return (
    <LineIcon {...props}>
      <circle cx="9" cy="8" r="3" />
      <circle cx="16.5" cy="9.5" r="2.3" />
      <path d="M4 19c0-2.8 2.2-5 5-5s5 2.2 5 5M15 14.2c2.3.2 4 2 4 4.3" />
    </LineIcon>
  );
}

export function IconRede(props: IconProps) {
  return (
    <LineIcon {...props}>
      <circle cx="12" cy="5" r="2.2" />
      <circle cx="5" cy="18" r="2.2" />
      <circle cx="19" cy="18" r="2.2" />
      <path d="M10.8 6.9 6.2 16M13.2 6.9l4.6 9.1M7.2 18h9.6" />
    </LineIcon>
  );
}

export function IconRegulamento(props: IconProps) {
  return (
    <LineIcon {...props}>
      <path d="M6 3h8l4 4v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
      <path d="M14 3v4h4" />
      <path d="M8.5 12.5h7M8.5 16h7M8.5 9h3" />
    </LineIcon>
  );
}

export function IconFaq(props: IconProps) {
  return (
    <LineIcon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.6 9.4a2.4 2.4 0 1 1 3.3 2.5c-.7.3-1 .8-1 1.6v.3" />
      <circle cx="11.9" cy="16.6" r=".6" fill="currentColor" stroke="none" />
    </LineIcon>
  );
}

export function IconDesafios(props: IconProps) {
  return (
    <LineIcon {...props}>
      <path d="M5 21V4M5 4h11l-2 3 2 3H5" />
      <circle cx="18" cy="17" r="3" />
      <path d="M18 14v-2M18 22v-2M21 17h1M14 17h1" />
    </LineIcon>
  );
}

export function IconManifesto(props: IconProps) {
  return (
    <LineIcon {...props}>
      <path d="M6 3h12a1 1 0 0 1 1 1v16l-7-3.5L5 20V4a1 1 0 0 1 1-1z" />
      <path d="M9 8.5h6M9 11.5h4" />
    </LineIcon>
  );
}

export function IconSair(props: IconProps) {
  return (
    <LineIcon {...props}>
      <path d="M14 4H6a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h8" />
      <path d="M17 8l4 4-4 4M9 12h12" />
    </LineIcon>
  );
}

/**
 * Glyphs de dimensão em linha (estilo 1:1 com o handoff Icons.jsx > DimGlyph).
 * Mapeia as 5 dimensões reais para ícones de traço colorido.
 */
function DimLineIcon({ color = 'currentColor', size = 24, children }: ColorIconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

const DIM_GLYPHS: Record<string, (c: string, s: number) => JSX.Element> = {
  // Colaboração — pessoas
  cooperacao: (c, s) => (
    <DimLineIcon color={c} size={s}>
      <circle cx="9" cy="8" r="3" />
      <circle cx="16.5" cy="9.5" r="2.3" />
      <path d="M4 19c0-2.8 2.2-5 5-5s5 2.2 5 5M15 14.2c2.3.2 4 2 4 4.3" />
    </DimLineIcon>
  ),
  // Inovação — lâmpada
  iniciativa: (c, s) => (
    <DimLineIcon color={c} size={s}>
      <path d="M9 17h6M10 21h4" />
      <path d="M12 3a6 6 0 0 0-3.5 10.9c.6.4.9 1 .9 1.6h5.2c0-.6.3-1.2.9-1.6A6 6 0 0 0 12 3z" />
    </DimLineIcon>
  ),
  // Foco no cliente — alvo
  integridade: (c, s) => (
    <DimLineIcon color={c} size={s}>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="1" fill={c} stroke="none" />
    </DimLineIcon>
  ),
  // Liderança — estrela
  lideranca: (c, s) => (
    <DimLineIcon color={c} size={s}>
      <path d="M12 4l2.3 4.7 5.2.8-3.8 3.7.9 5.2-4.6-2.5-4.6 2.5.9-5.2L4.5 9.5l5.2-.8z" />
    </DimLineIcon>
  ),
};

export function DimGlyph({ id, color = 'currentColor', size = 24 }: { id: string; color?: string; size?: number }) {
  const glyph = DIM_GLYPHS[id];
  return glyph ? glyph(color, size) : null;
}

export function IconSprout({ color = 'currentColor', size = 20 }: ColorIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 21v-8" />
      <path d="M12 13c0-3.2 2.1-5.4 5.3-5.4C17.3 10.8 15.2 13 12 13z" />
      <path d="M12 14c0-3.2-2.1-5.4-5.3-5.4C6.7 11.8 8.8 14 12 14z" />
    </svg>
  );
}

export function IconLeafSeed({ color = 'currentColor', size = 20 }: ColorIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 19c0-7 5-13 14-13 0 9-6 14-13 14" />
      <path d="M6 18c3-4 6-6 10-7" />
    </svg>
  );
}

export function IconPlus({ color = 'currentColor', size = 16 }: ColorIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

export function IconReplay({ color = 'currentColor', size = 16 }: ColorIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <path d="M3 3v5h5" />
    </svg>
  );
}

/**
 * Animação celebratória por nível (handoff Feeds.jsx > NivelAnimation):
 * semeou (sementes lançadas) / cresceu (folha crescendo) / floriu (flor abrindo).
 * As classes rb-* são animadas no styles.scss da página.
 */
export function NivelAnimation({ nivel, size = 76 }: { nivel: string; size?: number }) {
  if (nivel === 'semeou') {
    return (
      <svg viewBox="0 0 72 72" width={size} height={size}>
        <path d="M7 51 C 22 43, 50 43, 65 51 L65 63 L7 63 Z" fill="#754626" />
        <path d="M7 51 C 22 43, 50 43, 65 51 C 50 47, 22 47, 7 51 Z" fill="#9a6a41" />
        <g>
          <circle className="rb-seed" style={{ animationDelay: '0s' }} cx="24" cy="47" r="3" fill="#5aa53a" />
          <circle className="rb-seed" style={{ animationDelay: '.14s' }} cx="34" cy="49" r="3.1" fill="#caa24a" />
          <circle className="rb-seed" style={{ animationDelay: '.28s' }} cx="44" cy="47" r="3" fill="#6cbf46" />
          <circle className="rb-seed" style={{ animationDelay: '.42s' }} cx="52" cy="49" r="2.7" fill="#8a5a33" />
        </g>
      </svg>
    );
  }

  if (nivel === 'cresceu') {
    return (
      <svg viewBox="0 0 72 72" width={size} height={size}>
        <path className="rb-stem" d="M36 64 C36 50 36 42 36 26" stroke="#4a9e30" strokeWidth="4" fill="none" strokeLinecap="round" />
        <path className="rb-leaf" d="M36 44 C22 42 18 31 31 28 C35 34 36 39 36 44 Z" fill="#6cbf46" />
        <path className="rb-leaf2" d="M36 36 C50 34 55 23 42 20 C38 27 36 31 36 36 Z" fill="#4a9e30" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 72 72" width={size} height={size}>
      <path className="rb-stem" d="M36 66 L36 40" stroke="#4a9e30" strokeWidth="4" fill="none" strokeLinecap="round" />
      <path className="rb-leaf" d="M36 52 C26 50 24 42 34 41 C35 45 36 48 36 52 Z" fill="#5aa53a" />
      <ellipse
        className="rb-petal"
        style={{ ['--rot' as any]: '0deg', animationDelay: '.34s' }}
        cx="36"
        cy="20"
        rx="6"
        ry="12"
        fill="#f2803e"
      />
      <ellipse
        className="rb-petal"
        style={{ ['--rot' as any]: '72deg', animationDelay: '.42s' }}
        cx="36"
        cy="20"
        rx="6"
        ry="12"
        fill="#e0629b"
      />
      <ellipse
        className="rb-petal"
        style={{ ['--rot' as any]: '144deg', animationDelay: '.5s' }}
        cx="36"
        cy="20"
        rx="6"
        ry="12"
        fill="#f6c94a"
      />
      <ellipse
        className="rb-petal"
        style={{ ['--rot' as any]: '216deg', animationDelay: '.58s' }}
        cx="36"
        cy="20"
        rx="6"
        ry="12"
        fill="#ef6f5b"
      />
      <ellipse
        className="rb-petal"
        style={{ ['--rot' as any]: '288deg', animationDelay: '.66s' }}
        cx="36"
        cy="20"
        rx="6"
        ry="12"
        fill="#f39b3c"
      />
      <circle className="rb-core" cx="36" cy="34" r="6" fill="#f6c94a" />
    </svg>
  );
}
